/* Avora cart recommendations — pure reward-gap planning shared by UI and QA. */
(function (root, factory) {
  'use strict';

  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.AvoraCartUpsellCore = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var DEFAULT_TIERS = [
    { threshold: 9000, wallet: 2000, title: '€20 Gift Wallet' },
    { threshold: 15000, wallet: 4000, title: '€40 Gift Wallet' },
    { threshold: 25000, wallet: 7000, futureCard: 2500, title: '€70 Gift Wallet + €25 Reward Card' },
    { threshold: 35000, wallet: 11000, futureCard: 3500, title: '€110 Gift Wallet + €35 Reward Card' },
    { threshold: 50000, wallet: 17500, futureCard: 5000, title: '€175 Gift Wallet + €50 Reward Card' }
  ];

  function isRewardGift(item) {
    return !!(item && item.properties && (item.properties._avora_reward_gift === true || item.properties._avora_reward_gift === 'true'));
  }

  function paidSubtotal(cart) {
    return (cart && cart.items || []).reduce(function (total, item) {
      return isRewardGift(item) ? total : total + (Number(item.final_line_price) || 0);
    }, 0);
  }

  function paidOriginalSubtotal(cart) {
    return (cart && cart.items || []).reduce(function (total, item) {
      if (isRewardGift(item)) return total;
      var originalLinePrice = Number(item.original_line_price);
      if (!Number.isFinite(originalLinePrice)) {
        originalLinePrice = (Number(item.original_price) || 0) * (Number(item.quantity) || 0);
      }
      return total + (originalLinePrice || Number(item.final_line_price) || 0);
    }, 0);
  }

  function cartDiscountFactor(cart) {
    var originalSubtotal = paidOriginalSubtotal(cart);
    if (!originalSubtotal) return 1;
    return Math.min(1, Math.max(0, paidSubtotal(cart) / originalSubtotal));
  }

  function normaliseTiers(tiers) {
    var source = Array.isArray(tiers) && tiers.length ? tiers : DEFAULT_TIERS;
    return source
      .map(function (tier) {
        return {
          threshold: Number(tier.threshold) || 0,
          wallet: Number(tier.wallet) || 0,
          futureCard: Number(tier.futureCard || tier.future_card) || 0,
          futureMinimum: Number(tier.futureMinimum || tier.future_minimum) || 0,
          title: tier.title || ''
        };
      })
      .filter(function (tier) { return tier.threshold > 0; })
      .sort(function (a, b) { return a.threshold - b.threshold; });
  }

  function nextTierFor(subtotal, tiers) {
    return normaliseTiers(tiers).find(function (tier) { return subtotal < tier.threshold; }) || null;
  }

  function directAddPenalty(product) {
    return product.variantId ? 0 : 500;
  }

  function relevancePenalty(product) {
    return Math.min(600, Math.max(0, Number(product.rank) || 0) * 18);
  }

  function compareForGap(a, b, gap) {
    var aCrosses = a.price >= gap;
    var bCrosses = b.price >= gap;
    if (aCrosses !== bCrosses) return aCrosses ? -1 : 1;

    var aDistance = Math.abs(a.price - gap);
    var bDistance = Math.abs(b.price - gap);
    var aScore = aDistance + directAddPenalty(a) + relevancePenalty(a);
    var bScore = bDistance + directAddPenalty(b) + relevancePenalty(b);
    return aScore - bScore || a.price - b.price || a.id - b.id;
  }

  function eligibleProducts(products, cart) {
    var inCart = {};
    (cart && cart.items || []).forEach(function (item) {
      if (!isRewardGift(item)) inCart[Number(item.product_id)] = true;
    });

    return (products || []).filter(function (product) {
      return product && Number(product.id) && Number(product.price) > 0 && !inCart[Number(product.id)];
    });
  }

  function applyCartDiscountEstimate(product, discountFactor) {
    var copy = {};
    Object.keys(product).forEach(function (key) { copy[key] = product[key]; });
    var catalogPrice = Number(product.price) || 0;
    var estimatedPrice = discountFactor < 1 ? Math.ceil(catalogPrice * discountFactor) : catalogPrice;

    if (estimatedPrice < catalogPrice) {
      copy.compareAt = Math.max(Number(product.compareAt) || 0, catalogPrice);
      copy.price = estimatedPrice;
      copy.discountAdjusted = true;
    } else {
      copy.discountAdjusted = false;
    }
    return copy;
  }

  function bestPair(candidates, gap) {
    var best = null;
    var pool = candidates.slice(0, 80);

    for (var left = 0; left < pool.length; left += 1) {
      for (var right = left + 1; right < pool.length; right += 1) {
        if (pool[left].id === pool[right].id) continue;
        var total = pool[left].price + pool[right].price;
        if (total < gap) continue;

        var score = total - gap + directAddPenalty(pool[left]) + directAddPenalty(pool[right]) +
          relevancePenalty(pool[left]) + relevancePenalty(pool[right]);
        if (!best || score < best.score || (score === best.score && total < best.total)) {
          best = { products: [pool[left], pool[right]], total: total, score: score };
        }
      }
    }

    return best;
  }

  function annotate(product, gap, pairMode) {
    var copy = {};
    Object.keys(product).forEach(function (key) { copy[key] = product[key]; });
    copy.crossesNextTier = product.price >= gap;
    copy.remainingAfterAdd = Math.max(0, gap - product.price);
    copy.pairPath = !!pairMode;
    return copy;
  }

  function recommendationPlan(products, cart, tiers, limit) {
    var subtotal = paidSubtotal(cart || {});
    var discountFactor = cartDiscountFactor(cart || {});
    var nextTier = nextTierFor(subtotal, tiers);
    var candidates = eligibleProducts(products, cart || {}).map(function (product) {
      return applyCartDiscountEstimate(product, discountFactor);
    });
    var maximum = Math.max(1, Number(limit) || 2);

    if (!nextTier) {
      candidates.sort(function (a, b) {
        var aScore = directAddPenalty(a) + relevancePenalty(a) + Math.abs(a.price - 2500) * 0.25;
        var bScore = directAddPenalty(b) + relevancePenalty(b) + Math.abs(b.price - 2500) * 0.25;
        return aScore - bScore || a.price - b.price;
      });
      return {
        paidSubtotal: subtotal,
        discountFactor: discountFactor,
        nextTier: null,
        gap: 0,
        mode: 'complete',
        pairTotal: 0,
        products: candidates.slice(0, maximum).map(function (product) { return annotate(product, 0, false); })
      };
    }

    var gap = nextTier.threshold - subtotal;
    var sorted = candidates.slice().sort(function (a, b) { return compareForGap(a, b, gap); });
    var crossing = sorted.filter(function (product) { return product.price >= gap; });
    var pair = bestPair(candidates, gap);
    var chosen = [];
    var mode = 'progress';

    if (crossing.length) {
      var single = crossing[0];
      // Two products earn their extra interaction only when they save at least €4
      // versus the closest one-product unlock. This avoids artificial basket bloat.
      if (pair && pair.total + 400 < single.price) {
        chosen = pair.products.slice();
        mode = 'pair';
      } else {
        chosen = crossing.slice(0, maximum);
        mode = 'single';
      }
    } else if (pair) {
      chosen = pair.products.slice();
      mode = 'pair';
    } else {
      chosen = sorted.slice(0, maximum);
    }

    sorted.forEach(function (product) {
      if (chosen.length >= maximum) return;
      if (!chosen.some(function (selected) { return selected.id === product.id; })) chosen.push(product);
    });

    return {
      paidSubtotal: subtotal,
      discountFactor: discountFactor,
      nextTier: nextTier,
      gap: gap,
      mode: mode,
      pairTotal: mode === 'pair' && pair ? pair.total : 0,
      products: chosen.slice(0, maximum).map(function (product) { return annotate(product, gap, mode === 'pair'); })
    };
  }

  return {
    DEFAULT_TIERS: DEFAULT_TIERS,
    isRewardGift: isRewardGift,
    paidSubtotal: paidSubtotal,
    paidOriginalSubtotal: paidOriginalSubtotal,
    cartDiscountFactor: cartDiscountFactor,
    nextTierFor: nextTierFor,
    recommendationPlan: recommendationPlan
  };
});
